import { type JSX } from 'react';

import { consumerApi } from './api';
import { EmptyState, ErrorState, LoadingState, OfferGrid } from './components';
import { SearchBar } from './SearchBar';
import { usePageMeta } from './page-meta';
import { AppLink, useNavigator } from './router';
import { useResource } from './useResource';

/**
 * Browsing one category.
 *
 * The BFF answers this through the same path a search filtered to the category
 * takes, so a category page and that search can never disagree about what is in
 * it. Two read paths would eventually diverge, and the customer would meet the
 * divergence rather than us.
 *
 * The heading uses the category id until an offer arrives carrying a name,
 * rather than inventing a title from the slug - a page that confidently titles
 * itself from a URL fragment gets it wrong the first time somebody renames a
 * category.
 */
export function CategoryPage({ categoryId }: { readonly categoryId: string }): JSX.Element {
  const { location } = useNavigator();
  const search = location.query.toString();

  const resource = useResource(`category:${categoryId}?${search}`, (signal) =>
    consumerApi.category(categoryId, search.length > 0 ? `?${search}` : '', signal),
  );

  usePageMeta({
    title: 'Browse category',
    description: 'Browse products and services on Just Treated.',
    canonicalPath: `/categories/${categoryId}`,
    // Indexable: a category is a real, stable, public page, unlike a search.
    noindex: false,
  });

  return (
    <div className="jt-page jt-container">
      <h1 className="jt-page__title">Browse this category</h1>

      <SearchBar />

      {resource.status === 'loading' ? <LoadingState label="Loading…" /> : null}

      {resource.status === 'error' ? (
        <ErrorState message={resource.message} onRetry={resource.reload} />
      ) : null}

      {resource.status === 'loaded' ? (
        resource.data.results.length === 0 ? (
          <EmptyState title="Nothing is listed in this category yet.">
            <p>
              <AppLink className="jt-link" to="/search">
                Search the whole marketplace
              </AppLink>{' '}
              instead.
            </p>
          </EmptyState>
        ) : (
          <OfferGrid offers={resource.data.results} label="Category results" />
        )
      ) : null}
    </div>
  );
}
